import { FormEvent, useState } from "react";
import { ArrowRight, LockKeyhole, ShieldCheck, SunMedium } from "lucide-react";
import loginBackground from "@/assets/login-background.png";
import eagleLogo from "@/assets/eagle-logo-ea1.jpg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi, setAdminSecret } from "@/lib/api";

interface LoginPageProps {
  onReady: () => void;
}

export function LoginPage({ onReady }: LoginPageProps): import("react").ReactElement {
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setLoading(true);
    setError("");
    setAdminSecret(secret);
    try {
      await adminApi.bootstrap();
      onReady();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to unlock console");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#ebe2da] p-3 text-[#172339] sm:p-5 lg:p-8">
      <main className="relative min-h-[calc(100vh-1.5rem)] overflow-hidden rounded-[22px] bg-[#f7efe6] shadow-[0_30px_90px_rgba(79,54,35,0.16)] sm:min-h-[calc(100vh-2.5rem)] lg:min-h-[calc(100vh-4rem)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(244,158,65,0.22),transparent_28%),radial-gradient(circle_at_83%_18%,rgba(47,118,89,0.18),transparent_26%)]" />
        <div className="relative z-10 mx-auto flex min-h-[inherit] w-full max-w-7xl flex-col px-5 py-5 sm:px-8 sm:py-7 lg:px-12">
          <header className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src={eagleLogo}
                alt="Eagle Envi-Tech"
                className="h-14 w-auto rounded-sm object-contain mix-blend-multiply sm:h-16"
              />
            </div>
            <div className="hidden items-center gap-2 rounded-full border border-[#d9c8b7] bg-[#fffaf4]/75 px-4 py-2 text-sm font-semibold text-[#833921] shadow-sm sm:flex">
              <SunMedium className="h-4 w-4 text-[#d96426]" />
              <span>Palayamkottai operations</span>
            </div>
          </header>

          <section className="grid flex-1 items-center gap-8 py-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-0 lg:py-12">
            <div className="relative min-h-[420px] overflow-hidden rounded-[18px] border border-[#ead8c8] bg-[#d7c0a7] shadow-[0_28px_65px_rgba(78,52,31,0.22)] sm:min-h-[520px] lg:min-h-[600px] lg:rounded-r-none">
              <img
                src={loginBackground}
                alt="Solar powered home"
                className="absolute inset-0 h-full w-full object-cover object-[43%_50%]"
              />
              <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,37,39,0.08),rgba(255,246,236,0.14)_70%,rgba(255,246,236,0.42))]" />
              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-4 bg-gradient-to-t from-[#172339]/70 via-[#172339]/20 to-transparent p-5 text-[#fffaf2] sm:p-8">
                <div className="max-w-xl">
                  <p className="text-xs font-bold uppercase tracking-normal text-[#ffd9a8]">Eagle Envitech</p>
                  <h1 className="mt-3 max-w-2xl text-3xl font-bold leading-tight sm:text-5xl">
                    Eagle Envitech command center
                  </h1>
                </div>
                <div className="hidden rounded-full bg-[#fff6e8] px-4 py-2 text-sm font-bold text-[#3c160f] shadow-lg md:block">
                  Admin access
                </div>
              </div>
            </div>

            <aside className="relative mx-auto w-full max-w-md lg:-ml-16">
              <div className="absolute -inset-5 rounded-[30px] bg-[linear-gradient(145deg,rgba(255,248,239,0.64),rgba(245,198,172,0.52),rgba(255,251,245,0.72))] blur-xl" />
              <form
                className="relative space-y-7 rounded-[18px] border border-[#fff7ee] bg-[#fff9f2]/92 px-6 py-7 shadow-[0_26px_80px_rgba(69,35,20,0.22)] backdrop-blur sm:px-8 sm:py-9"
                onSubmit={submit}
              >
                <div className="space-y-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#f3ddc6] text-[#9a3a19]">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold tracking-normal text-[#7b2b1d] sm:text-3xl">Owner console</h2>
                    <p className="mt-2 text-sm leading-6 text-[#755a4d]">
                      Enter the tenant admin secret to open the owner dashboard.
                    </p>
                  </div>
                </div>

                <label className="block space-y-2 text-sm font-semibold text-[#4f3427]">
                  <span>Admin secret</span>
                  <input
                    type="hidden"
                    name="username"
                    autoComplete="username"
                    value="eagle-envitech-admin"
                    readOnly
                  />
                  <Input
                    autoFocus
                    autoComplete="current-password"
                    type="password"
                    value={secret}
                    onChange={(event) => setSecret(event.target.value)}
                    placeholder="X-Admin-Secret"
                    className="h-14 rounded-[10px] border-[#ead2bf] bg-[#fffefd] px-4 text-base shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] placeholder:text-[#ad9486] focus:ring-[#d66b2a]"
                  />
                </label>

                {error && (
                  <p className="rounded-[10px] border border-[#f0b6a2] bg-[#fff0ec] px-4 py-3 text-sm font-medium text-[#9b2f1e]">
                    {error}
                  </p>
                )}

                <Button
                  className="h-14 w-full rounded-[10px] bg-[#3a0909] px-5 text-base font-bold text-[#fff8ee] shadow-[0_16px_28px_rgba(58,9,9,0.24)] hover:bg-[#5a1610]"
                  disabled={!secret || loading}
                >
                  <LockKeyhole className="h-5 w-5" />
                  <span>{loading ? "Checking..." : "Unlock dashboard"}</span>
                  <ArrowRight className="ml-auto h-5 w-5" />
                </Button>
              </form>
            </aside>
          </section>
        </div>
      </main>
    </div>
  );
}
